/**
 * DashboardRangeContext — shared selected date range for the home dashboard.
 *
 * The header's date selector sets the range; every widget hook reads it
 * via useDashboardRange() and includes it in its React Query cache key,
 * so picking a new range automatically refetches the affected widgets.
 *
 * Shape: `{ key, fromDate?, toDate? }`.
 *  - For the four quick keys (today / this_week / this_month / last_month),
 *    `fromDate` and `toDate` are undefined — the BE derives them from `key`.
 *  - For `custom`, the FE sends explicit `fromDate` + `toDate` (YYYY-MM-DD)
 *    and the BE uses those verbatim.
 *
 * The context has a safe default ('today') so widget hooks still work in
 * tests or stories that render a single widget without the provider.
 */

import * as React from "react";

export const DASHBOARD_RANGE_KEYS = [
  "today",
  "this_week",
  "this_month",
  "last_month",
  "custom",
] as const;

export type DashboardRangeKey = (typeof DASHBOARD_RANGE_KEYS)[number];

export const DEFAULT_DASHBOARD_RANGE: DashboardRangeKey = "today";

export const DASHBOARD_RANGE_LABELS: Record<DashboardRangeKey, string> = {
  today: "Today",
  this_week: "This Week",
  this_month: "This Month",
  last_month: "Last Month",
  custom: "Custom",
};

export interface DashboardRangeValue {
  key: DashboardRangeKey;
  /** Only populated when key === "custom". YYYY-MM-DD. */
  fromDate?: string;
  /** Only populated when key === "custom". YYYY-MM-DD. */
  toDate?: string;
}

interface DashboardRangeContextValue {
  range: DashboardRangeValue;
  setRange: (next: DashboardRangeValue) => void;
}

const DashboardRangeContext = React.createContext<DashboardRangeContextValue>({
  range: { key: DEFAULT_DASHBOARD_RANGE },
  setRange: () => {
    // No-op default — useful when a widget is rendered outside the
    // provider (tests, isolated stories). Production tree always mounts
    // DashboardRangeProvider above the widget grid.
  },
});

export function DashboardRangeProvider({
  children,
  initial = { key: DEFAULT_DASHBOARD_RANGE },
}: {
  children: React.ReactNode;
  initial?: DashboardRangeValue;
}) {
  const [range, setRange] = React.useState<DashboardRangeValue>(initial);
  const value = React.useMemo(() => ({ range, setRange }), [range]);
  return (
    <DashboardRangeContext.Provider value={value}>
      {children}
    </DashboardRangeContext.Provider>
  );
}

/** Read the currently selected range (used by widget hooks for cache keys + fetch params). */
export const useDashboardRange = (): DashboardRangeValue =>
  React.useContext(DashboardRangeContext).range;

/** Read + write the range (use this in the header selector). */
export const useDashboardRangeControl = (): DashboardRangeContextValue =>
  React.useContext(DashboardRangeContext);
